import { motion } from "motion/react";
import { Menu, X, Shield, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";

const SKOOL_URL = "https://www.skool.com/letter-writers-society-5504";

export default function Navbar() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("");
  const [showAdminMenu, setShowAdminMenu] = useState(false);

  useEffect(() => {
    const handleHashChange = () => {
      setActiveHash(window.location.hash || "#home");
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const navItems = [
    { name: "Events", href: "#events" },
    { name: "Chapters", href: "#chapters" },
    { name: "Pen Pals", href: "#penpal" },
    { name: "Courses", href: "#courses" },
    { name: "Resources", href: "#resources", memberOnly: true },
    { name: "Shop", href: "#shop" },
    { name: "The Seasonal Post", href: "#seasonal-post" },
    { name: "Read", href: "#read" },
    { name: "Community", href: SKOOL_URL, external: true },
    { name: "Prompts", href: "#prompts" },
    { name: "About", href: "#about" },
  ];

  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-lws-paper/80 backdrop-blur-md border-b border-lws-ink/5" data-testid="navbar">
      {/* Challenge Banner */}
      <a 
        href="#challenge" 
        className="block bg-lws-amber text-lws-paper py-2 px-6 text-center text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-lws-rose transition-colors"
        data-testid="challenge-banner"
      >
        Join the 30-Day Correspondence Challenge — Sign up for prompts & stationery
      </a>

      <div className="max-w-7xl mx-auto px-6">
        {/* Logo Row - Elevated above navigation */}
        <div className="flex items-center justify-between py-4 border-b border-lws-ink/5">
          <a href="#home" className="font-serif text-2xl tracking-tighter text-lws-green hover:opacity-80 transition-opacity" data-testid="logo">
            Letter Writers Society
          </a>
          
          {/* User actions on logo row */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="#membership"
              className="bg-lws-green text-lws-paper px-5 py-2 text-xs uppercase tracking-widest font-medium hover:bg-lws-navy transition-colors"
              data-testid="join-society-btn"
            >
              Membership
            </a>
            
            {/* Admin Menu */}
            {user?.role === 'admin' && (
              <div className="relative">
                <button
                  onClick={() => setShowAdminMenu(!showAdminMenu)}
                  className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-lws-taupe hover:text-lws-green transition-colors font-medium"
                  data-testid="admin-menu-btn"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </button>
                {showAdminMenu && (
                  <div className="absolute right-0 top-full mt-2 bg-white border border-stone-200 rounded-lg shadow-lg py-2 min-w-[180px] z-50">
                    <a
                      href="#admin-system"
                      onClick={() => setShowAdminMenu(false)}
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 font-medium"
                    >
                      System Dashboard
                    </a>
                    <div className="border-t border-stone-100 my-1"></div>
                    <a
                      href="#admin-events"
                      onClick={() => setShowAdminMenu(false)}
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    >
                      Events
                    </a>
                    <a
                      href="#admin-resources"
                      onClick={() => setShowAdminMenu(false)}
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    >
                      Resources
                    </a>
                    <a
                      href="#admin-seasonal-post"
                      onClick={() => setShowAdminMenu(false)}
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    >
                      Seasonal Post
                    </a>
                    <a
                      href="#admin-forwarding"
                      onClick={() => setShowAdminMenu(false)}
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    >
                      Forwarding Admin
                    </a>
                    <a
                      href="#admin-reports"
                      onClick={() => setShowAdminMenu(false)}
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    >
                      Pen Pal Reports
                    </a>
                    <a
                      href="#admin-postmaster"
                      onClick={() => setShowAdminMenu(false)}
                      className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                    >
                      Postmaster Applications
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Toggle */}
          <button 
            className="md:hidden text-lws-ink p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Navigation Row - Below logo with more room */}
        <div className="hidden md:flex items-center justify-center py-3 gap-10">
          {navItems.map((item) => {
            // Skip member-only items if not logged in
            if (item.memberOnly && !user) return null;
            
            const isActive = activeHash === item.href || (activeHash === "#home" && item.href === "#home") || (activeHash === "" && item.href === "#home");
            
            // External link (opens in new tab)
            if (item.external) {
              return (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className="text-xs uppercase tracking-[0.2em] transition-all font-medium py-1 text-lws-taupe hover:text-lws-green flex items-center gap-1"
                >
                  {item.name}
                  <ExternalLink className="w-3 h-3" />
                </a>
              );
            }
            
            return (
              <a
                key={item.name}
                href={item.href}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className={`text-xs uppercase tracking-[0.2em] transition-all font-medium relative py-1 ${
                  isActive ? "text-lws-navy" : "text-lws-taupe hover:text-lws-green"
                }`}
              >
                {item.name}
                {isActive && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute bottom-0 left-0 w-full h-[1px] bg-lws-navy"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </a>
            );
          })}
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden absolute top-full left-0 w-full bg-lws-paper border-b border-lws-ink/5 px-6 py-10 flex flex-col space-y-6"
          data-testid="mobile-menu"
        >
          {navItems.map((item) => {
            // Skip member-only items if not logged in
            if (item.memberOnly && !user) return null;
            
            const isActive = activeHash === item.href || (activeHash === "#home" && item.href === "#home");
            
            // External link (opens in new tab)
            if (item.external) {
              return (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="text-lg font-serif transition-colors inline-flex items-center gap-2 w-fit text-lws-ink hover:text-lws-green"
                >
                  {item.name}
                  <ExternalLink className="w-4 h-4" />
                </a>
              );
            }
            
            return (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setIsOpen(false)}
                className={`text-lg font-serif transition-colors relative inline-block w-fit ${
                  isActive ? "text-lws-navy" : "text-lws-ink hover:text-lws-green"
                }`}
              >
                {item.name}
                {isActive && (
                  <div className="absolute -bottom-1 left-0 w-full h-[1px] bg-lws-navy" />
                )}
              </a>
            );
          })}
          <a
            href="#membership"
            onClick={() => setIsOpen(false)}
            className="bg-lws-green text-lws-paper px-6 py-4 text-center text-xs uppercase tracking-widest font-medium"
          >
            Membership
          </a>
        </motion.div>
      )}
    </nav>
  );
}
